/**
 * Theme → CSS custom properties (SPEC §6).
 *
 * `ui.bg.panel` becomes `--ui-bg-panel` and `font.sans` becomes `--font-sans`.
 * Axis colors and the scalar viewport colors (labels, selection, gizmo) also
 * become `--axis-*` / `--vp-*`, for DOM overlays drawn over the viewport
 * (architecture §6.3). The sky gradient and grid stay renderer-only.
 * Shared by the runtime ThemeService and the build-time static shell, so
 * the page is themed correctly even before any JavaScript runs.
 */
import type { Theme } from "../../themes/index.ts";

export function flattenThemeToCssVars(theme: Theme): Record<string, string> {
  const vars: Record<string, string> = {};

  for (const [key, value] of Object.entries(theme.ui)) {
    vars[`--ui-${cssName(key)}`] = value;
  }
  for (const [key, value] of Object.entries(theme.font)) {
    vars[`--font-${key}`] = value;
  }
  for (const [key, value] of Object.entries(theme.axis)) {
    vars[`--axis-${key}`] = value;
  }
  for (const [key, value] of Object.entries(theme.viewport)) {
    if (typeof value === "string") vars[`--vp-${cssName(key)}`] = value;
  }

  return vars;
}

/** A CSS rule assigning the theme's variables (plus `color-scheme`) to `selector`. */
export function themeCssBlock(selector: string, theme: Theme): string {
  const declarations = Object.entries(flattenThemeToCssVars(theme)).map(
    ([name, value]) => `  ${name}: ${value};`,
  );
  declarations.push(`  color-scheme: ${theme.mode};`);
  return `${selector} {\n${declarations.join("\n")}\n}`;
}

/** Theme keys are dotted ("label.bg"); CSS custom properties use dashes. */
export function cssName(key: string): string {
  return key.replaceAll(".", "-");
}
